import { Cli, z } from "incur";
import { availableAccountsMessage, profileAccount } from "./account-selection";
import { readConfig } from "./config";
import { getSecret } from "./secret-store";
import { listOAuthAccounts } from "./oauth";
import {
	loginAdapter,
	loginEnv,
	loginExamples,
	loginHint,
	loginOptions,
	loginOutput,
} from "./login";
import {
	getActiveProfile,
	getValidAccessToken,
	listProfiles,
	removeAllProfiles,
	removeProfile,
	setActiveProfileAccount,
	switchProfile,
} from "./profiles";
import {
	type AccountChoice,
	PromptCancelledError,
	selectAccount,
	selectProfile,
	startSpinner,
} from "./prompts";
import {
	AuthMethod,
	type CtaInput,
	type ErrorInput,
	Identity,
	Profile,
} from "./types";

/**
 * Auth commands: `whop auth login | logout | switch | account | status | list`.
 *
 * The login/logout business logic is context-free (`performLogin` in login.ts,
 * `removeProfile`) and the incur run handlers are thin adapters. `loginAdapter`
 * and `logoutAdapter` are shared so the top-level `whop login` / `whop logout`
 * (registered in `index.ts`) behave identically without duplicating logic —
 * incur aliases can't cross from a group into a top-level command.
 */

export const logoutOptions = z.object({
	profile: z
		.string()
		.optional()
		.describe("Profile to remove (defaults to the active profile)"),
	all: z
		.boolean()
		.optional()
		.describe("Remove every profile and its stored secret (full reset)"),
});
export const logoutOutput = z.object({
	removed: z.array(z.string()).describe("The removed profile name(s)"),
	newActive: z
		.string()
		.nullable()
		.describe("The profile that is now active, if any"),
});
export type LogoutOutput = z.infer<typeof logoutOutput>;

const statusOutput = z.object({
	loggedIn: z
		.boolean()
		.describe("Whether the active profile has a usable stored credential"),
	profile: z.string().nullable(),
	method: AuthMethod.nullable(),
	identity: Identity.nullable().describe(
		"The logged-in user (OAuth) or the account (API key)",
	),
	account: Identity.nullable().describe(
		"The account auto-filled for scoped commands (account_id/company_id)",
	),
	hasSecret: z
		.boolean()
		.describe("Whether a secret was found in the OS keychain for this profile"),
});

const listOutput = z.object({
	active: z.string().nullable(),
	profiles: z.array(Profile),
});

const switchArgs = z.object({
	name: z
		.string()
		.optional()
		.describe("Profile to switch to (interactive picker if omitted)"),
});
const switchOutput = z.object({
	active: z.string().describe("The newly active profile"),
});

const accountArgs = z.object({
	id: z
		.string()
		.optional()
		.describe(
			"Business account to make the default — id (biz_xxx) or route (interactive picker if omitted)",
		),
});
const accountOptions = z.object({
	list: z
		.boolean()
		.optional()
		.describe(
			"List the business accounts you can manage without changing the default",
		),
});
const accountListItem = z.object({
	id: z.string(),
	title: z.string(),
	route: z.string(),
	active: z
		.boolean()
		.describe("Whether this is the profile's default business account"),
});
const accountOutput = z.object({
	profile: z.string().describe("The active profile"),
	account: Identity.nullable().describe(
		"The default business account passed to scoped commands",
	),
	accounts: z
		.array(accountListItem)
		.describe("Business accounts this user can manage"),
});

type LogoutAdapterCtx = {
	options: { profile?: string | undefined; all?: boolean | undefined };
	ok: (data: LogoutOutput, meta?: { cta?: CtaInput | undefined }) => never;
	error: (options: ErrorInput) => never;
};

export function logoutAdapter(c: LogoutAdapterCtx): never {
	if (c.options.all) {
		if (c.options.profile) {
			return c.error({
				code: "CONFLICTING_OPTIONS",
				message:
					"--all removes every profile — don't combine it with --profile.",
				retryable: true,
			});
		}
		const removed = removeAllProfiles();
		if (removed.length === 0) {
			return c.error({
				code: "NO_PROFILES",
				message: "No profiles to remove.",
				retryable: false,
				cta: {
					description: "Log in first:",
					commands: [{ command: "auth login", description: "Log in to Whop" }],
				},
			});
		}
		return c.ok(
			{ removed, newActive: null },
			{
				cta: {
					description: `Removed ${removed.length} profile${removed.length === 1 ? "" : "s"}. To authenticate again:`,
					commands: [{ command: "auth login", description: "Log in to Whop" }],
				},
			},
		);
	}

	const target = c.options.profile ?? getActiveProfile()?.name;
	if (!target) {
		return c.error({
			code: "NO_ACTIVE_PROFILE",
			message: "No active profile to log out from.",
			retryable: false,
			cta: {
				description: "Log in first:",
				commands: [{ command: "auth login", description: "Log in to Whop" }],
			},
		});
	}
	const { removed, newActive } = removeProfile(target);
	if (!removed) {
		return c.error({
			code: "PROFILE_NOT_FOUND",
			message: `No profile named "${target}".`,
			retryable: false,
		});
	}
	return c.ok(
		{ removed: [target], newActive },
		newActive
			? {
					cta: {
						description: `Now using "${newActive}".`,
						commands: [
							{
								command: "auth status",
								description: "Show the active identity",
							},
						],
					},
				}
			: undefined,
	);
}

// --- The `auth` command group ---
export function buildAuthGroup() {
	const auth = Cli.create("auth", {
		description: "Manage Whop authentication",
	});

	auth.command("login", {
		description: "Log in to Whop",
		aliases: ["add"],
		options: loginOptions,
		env: loginEnv,
		output: loginOutput,
		examples: loginExamples,
		hint: loginHint,
		run: (c) => loginAdapter(c),
	});

	auth.command("logout", {
		description: "Log out — remove a saved profile, or all with --all",
		aliases: ["remove", "rm"],
		options: logoutOptions,
		output: logoutOutput,
		examples: [
			{ description: "Remove the active profile" },
			{
				options: { all: true },
				description: "Remove every profile (full reset)",
			},
		],
		run: (c) => logoutAdapter(c),
	});

	auth.command("switch", {
		description: "Switch the active profile",
		args: switchArgs,
		output: switchOutput,
		run: async (c) => {
			const profiles = listProfiles();
			if (profiles.length === 0) {
				return c.error({
					code: "NO_PROFILES",
					message: "No profiles yet — log in first.",
					retryable: true,
					cta: {
						description: "To authenticate:",
						commands: [
							{ command: "auth login", description: "Log in to Whop" },
						],
					},
				});
			}

			let name = c.args.name;
			if (!name) {
				if (c.agent) {
					return c.error({
						code: "PROFILE_REQUIRED",
						message: `Specify a profile name. Available: ${profiles.map((p) => p.name).join(", ")}.`,
						retryable: true,
					});
				}
				try {
					name = await selectProfile(profiles);
				} catch (error) {
					if (error instanceof PromptCancelledError) {
						return c.error({
							code: "CANCELLED",
							message: "Switch cancelled.",
							exitCode: 130,
						});
					}
					throw error;
				}
			}

			const profile = switchProfile(name);
			if (!profile) {
				return c.error({
					code: "PROFILE_NOT_FOUND",
					message: `No profile named "${name}". Available: ${profiles.map((p) => p.name).join(", ")}.`,
					retryable: true,
				});
			}
			return c.ok(
				{ active: profile.name },
				{
					cta: {
						commands: [
							{
								command: "auth status",
								description: "Show the active identity",
							},
						],
					},
				},
			);
		},
	});

	auth.command("account", {
		description:
			"List your business accounts or set the default one for the active profile",
		aliases: ["accounts"],
		args: accountArgs,
		options: accountOptions,
		output: accountOutput,
		examples: [
			{ description: "Pick the default business account interactively" },
			{
				options: { list: true },
				description: "List the business accounts you can manage",
			},
			{
				args: { id: "biz_xxx" },
				description: "Set the default business account",
			},
		],
		hint: "OAuth profiles only — API key profiles are tied to a single business account.",
		run: async (c) => {
			const profile = getActiveProfile();
			if (!profile) {
				return c.error({
					code: "NO_ACTIVE_PROFILE",
					message: "No active profile — log in first.",
					retryable: true,
					cta: {
						description: "To authenticate:",
						commands: [
							{ command: "auth login", description: "Log in to Whop" },
						],
					},
				});
			}
			if (profile.method !== "oauth") {
				return c.error({
					code: "API_KEY_PROFILE",
					message:
						"API key profiles are tied to a single business account. Log in with Whop (OAuth) to switch between the businesses you manage.",
					retryable: false,
					cta: {
						description: "To use a different business account:",
						commands: [
							{
								command: "auth login",
								description: 'Choose "Log in with Whop"',
							},
						],
					},
				});
			}

			let token: string | null;
			try {
				token = await getValidAccessToken(profile);
			} catch {
				token = null;
			}
			if (!token) {
				return c.error({
					code: "SESSION_EXPIRED",
					message: "Your session is no longer valid — log in again.",
					retryable: false,
					cta: {
						description: "To authenticate:",
						commands: [
							{ command: "auth login", description: "Log in to Whop" },
						],
					},
				});
			}

			const s = c.agent
				? null
				: startSpinner("Loading your business accounts…");
			let accounts: AccountChoice[];
			try {
				accounts = await listOAuthAccounts(token);
				s?.stop(
					`Found ${accounts.length} business account${accounts.length === 1 ? "" : "s"}.`,
				);
			} catch {
				s?.stop("Couldn't load your business accounts.");
				return c.error({
					code: "ACCOUNT_LOOKUP_FAILED",
					message: "Couldn't load your business accounts. Please try again.",
					retryable: true,
				});
			}
			if (accounts.length === 0) {
				return c.error({
					code: "NO_ACCOUNTS",
					message: "You don't have a business account yet.",
					retryable: false,
					cta: {
						description: "Create your first business account:",
						commands: [
							{
								command: "quickstart",
								description: "Create and select a business account",
							},
						],
					},
				});
			}

			const currentAccount = profileAccount(profile);

			if (c.options.list) {
				return c.ok(
					{
						profile: profile.name,
						account: currentAccount,
						accounts: accounts.map((account) => ({
							...account,
							active: account.id === profile.accountId,
						})),
					},
					{
						cta: {
							description: "Change the default business account:",
							commands: [
								{
									command: "auth account",
									args: { id: true },
									description: "Pass a business account id or route",
								},
							],
						},
					},
				);
			}

			let requested = c.args.id;
			if (!requested) {
				if (c.agent) {
					return c.error({
						code: "ACCOUNT_REQUIRED",
						message: availableAccountsMessage(
							"Specify a business account.",
							accounts,
						),
						retryable: true,
					});
				}
				try {
					requested = (await selectAccount(accounts, profile.accountId)).id;
				} catch (error) {
					if (error instanceof PromptCancelledError) {
						return c.error({
							code: "CANCELLED",
							message: "Switch cancelled.",
							exitCode: 130,
						});
					}
					throw error;
				}
			}

			const match =
				accounts.find((account) => account.id === requested) ??
				accounts.find((account) => account.route === requested);
			if (!match) {
				return c.error({
					code: "ACCOUNT_NOT_FOUND",
					message: availableAccountsMessage(
						`No business account "${requested}" for this user.`,
						accounts,
					),
					retryable: true,
				});
			}

			if (!setActiveProfileAccount(match, profile.name)) {
				return c.error({
					code: "PROFILE_REMOVED",
					message: `Profile "${profile.name}" was removed while switching — log in again.`,
					retryable: false,
					cta: {
						description: "To authenticate:",
						commands: [
							{ command: "auth login", description: "Log in to Whop" },
						],
					},
				});
			}
			return c.ok(
				{
					profile: profile.name,
					account: match,
					accounts: accounts.map((account) => ({
						...account,
						active: account.id === match.id,
					})),
				},
				{
					cta: {
						description: `Now acting on behalf of ${match.title}.`,
						commands: [
							{
								command: "auth status",
								description: "Show the active identity",
							},
						],
					},
				},
			);
		},
	});

	auth.command("status", {
		description: "Show the active identity (whoami)",
		aliases: ["whoami"],
		output: statusOutput,
		run: async (c) => {
			const profile = getActiveProfile();
			if (!profile) {
				return c.ok(
					{
						loggedIn: false,
						profile: null,
						method: null,
						identity: null,
						account: null,
						hasSecret: false,
					},
					{
						cta: {
							description: "To authenticate:",
							commands: [
								{ command: "auth login", description: "Log in to Whop" },
							],
						},
					},
				);
			}
			const hasSecret = getSecret(profile.name) !== null;
			// "Logged in" means we can actually obtain a Bearer token. For oauth
			// this may refresh the access token; a revoked/expired refresh token
			// resolves to null instead of throwing, so the reported state is honest.
			let loggedIn = hasSecret;
			if (hasSecret) {
				try {
					loggedIn = (await getValidAccessToken(profile)) !== null;
				} catch {
					loggedIn = false;
				}
			}
			const account = profileAccount(profile);
			return c.ok(
				{
					loggedIn,
					profile: profile.name,
					method: profile.method,
					identity:
						profile.method === "oauth"
							? { id: profile.userId, email: profile.userEmail }
							: account,
					account,
					hasSecret,
				},
				loggedIn
					? undefined
					: {
							cta: {
								description: hasSecret
									? "Your session is no longer valid — log in again:"
									: "The stored secret is missing — log in again:",
								commands: [
									{ command: "auth login", description: "Log in to Whop" },
								],
							},
						},
			);
		},
	});

	auth.command("list", {
		description: "List saved authentication profiles",
		aliases: ["ls"],
		output: listOutput,
		run: () => {
			const { activeProfile, profiles } = readConfig();
			const active =
				profiles.find((p) => p.name === activeProfile)?.name ?? null;
			return { active, profiles };
		},
	});

	return auth;
}
